import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  Cloud as CloudIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PermissionUtils, StorageSettings as StorageSettingsType } from './types';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

const EMPTY_STORAGE: StorageSettingsType = {
  destination_type: 'S3',
  bucket_name: '',
  access_key: '',
  secret_key: '',
  cloud_region: '',
  upload_file_path: '',
};

const AWS_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-east-2', label: 'US East (Ohio)' },
  { value: 'us-west-1', label: 'US West (N. California)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'af-south-1', label: 'Africa (Cape Town)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { value: 'eu-west-1', label: 'Europe (Ireland)' },
  { value: 'eu-west-2', label: 'Europe (London)' },
  { value: 'eu-west-3', label: 'Europe (Paris)' },
  { value: 'eu-north-1', label: 'Europe (Stockholm)' },
  { value: 'me-south-1', label: 'Middle East (Bahrain)' },
  { value: 'sa-east-1', label: 'South America (São Paulo)' },
];

interface StorageSettingsProps {
  organizationId: string;
  applicationId: string;
  permissionUtils: PermissionUtils;
}

const mapFromApi = (data: any): StorageSettingsType => {
  const source = data?.userUploadDetails || data?.uploadDetails || data;
  const details = source?.upload_details || source;
  const destination = details?.destination_dtls || {};

  return {
    destination_type: 'S3',
    bucket_name: destination.s3_bucket_name || details.bucket_name || '',
    access_key: destination.s3_access_key || details.access_key || '',
    secret_key: destination.s3_secret_key || details.secret_key || '',
    cloud_region: destination.s3_cloud_region || details.cloud_region || '',
    upload_file_path: destination.se_upload_file_path
      || destination.s3_upload_file_path
      || details.upload_file_path
      || '',
  };
};

const StorageSettings: React.FC<StorageSettingsProps> = ({
  organizationId,
  applicationId,
  permissionUtils,
}) => {
  const { canEditSection } = permissionUtils;
  const canEdit = canEditSection('storage');

  const [storageSettings, setStorageSettings] = useState<StorageSettingsType>(EMPTY_STORAGE);
  const [snapshot, setSnapshot] = useState<StorageSettingsType>(EMPTY_STORAGE);
  const [hasExisting, setHasExisting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof StorageSettingsType, string>>>({});

  const fieldsDisabled = !canEdit || !isEditing;

  const fetchStorageDetails = useCallback(async () => {
    if (!organizationId || !applicationId) {
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post<any>(`${API_BASE_URL}/user/getUserStorageDetails`, payload);
      if (response.data?.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<any>(response.data.data, getSessionKey() as string);
        const mapped = mapFromApi(decrypted);
        setStorageSettings(mapped);
        setSnapshot(mapped);
        setHasExisting(true);
      } else if (response.data?.status === 404) {
        setStorageSettings(EMPTY_STORAGE);
        setSnapshot(EMPTY_STORAGE);
        setHasExisting(false);
      } else {
        setStorageSettings(EMPTY_STORAGE);
        setSnapshot(EMPTY_STORAGE);
        setHasExisting(false);
      }
    } catch (error: any) {
      const status = error?.response?.data?.status || error?.response?.status;
      if (status === 404) {
        setStorageSettings(EMPTY_STORAGE);
        setSnapshot(EMPTY_STORAGE);
        setHasExisting(false);
      } else {
        toast.error('Failed to load storage details.', { position: 'top-right' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, applicationId]);

  useEffect(() => {
    fetchStorageDetails();
    setIsEditing(false);
    setShowSecretKey(false);
    setErrors({});
  }, [fetchStorageDetails]);

  const handleChange = (field: keyof StorageSettingsType) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setStorageSettings(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const nextErrors: Partial<Record<keyof StorageSettingsType, string>> = {};
    if (!storageSettings.bucket_name.trim()) {
      nextErrors.bucket_name = 'Bucket name is required';
    }
    if (!storageSettings.access_key.trim()) {
      nextErrors.access_key = 'Access key is required';
    }
    if (!storageSettings.secret_key.trim()) {
      nextErrors.secret_key = 'Secret key is required';
    }
    if (!storageSettings.cloud_region.trim()) {
      nextErrors.cloud_region = 'Region is required';
    }
    if (!storageSettings.upload_file_path.trim()) {
      nextErrors.upload_file_path = 'Upload file path is required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const startEdit = () => {
    setSnapshot(storageSettings);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setStorageSettings(snapshot);
    setErrors({});
    setIsEditing(false);
    setShowSecretKey(false);
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          application_id: applicationId,
          storage_details: {
            destination_type: 'S3',
            bucket_name: storageSettings.bucket_name.trim(),
            access_key: storageSettings.access_key.trim(),
            secret_key: storageSettings.secret_key.trim(),
            cloud_region: storageSettings.cloud_region.trim(),
            upload_file_path: storageSettings.upload_file_path.trim(),
          },
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post<any>(`${API_BASE_URL}/user/updateUserStorageDetails`, payload);
      if (response.data?.status === 200 && response.data.data) {
        const decrypted = await decryptResponse<any>(response.data.data, getSessionKey() as string);
        const mapped = mapFromApi(decrypted);
        setStorageSettings(mapped);
        setSnapshot(mapped);
        setHasExisting(true);
        setIsEditing(false);
        setShowSecretKey(false);
        toast.success('Storage details saved successfully!', { position: 'top-right' });
      } else {
        const message = response.data?.error?.errorDetails || 'Failed to save storage details.';
        toast.error(message, { position: 'top-right' });
      }
    } catch (error: any) {
      const message = error?.response?.data?.error?.errorDetails || 'Failed to save storage details.';
      toast.error(message, { position: 'top-right' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent sx={{ overflow: 'visible' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CloudIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Storage Configuration</Typography>
          {!canEdit && (
            <Chip label="Read Only" color="default" size="small" sx={{ ml: 2 }} />
          )}
          {canEdit && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              {isEditing ? (
                <>
                  <Tooltip title={isSaving ? 'Saving...' : 'Save'}>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        size="small"
                        aria-label="Save storage configuration"
                      >
                        <SaveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <span>
                      <IconButton
                        onClick={cancelEdit}
                        disabled={isSaving}
                        size="small"
                        aria-label="Cancel storage configuration edit"
                      >
                        <CancelIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title={hasExisting ? 'Edit' : 'Add storage configuration'}>
                  <IconButton
                    color="primary"
                    onClick={startEdit}
                    disabled={isLoading}
                    size="small"
                    aria-label="Edit storage configuration"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <>
            {!hasExisting && (
              <Alert severity="info" sx={{ mb: 3 }}>
                No storage configuration found for this application. Add Amazon S3 details to store uploaded files.
              </Alert>
            )}

            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Destination Type"
                  value="Amazon S3"
                  disabled
                  variant="outlined"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={
                    storageSettings.cloud_region
                      && !AWS_REGIONS.some((region) => region.value === storageSettings.cloud_region)
                      ? [{ value: storageSettings.cloud_region, label: storageSettings.cloud_region }, ...AWS_REGIONS]
                      : AWS_REGIONS
                  }
                  value={
                    AWS_REGIONS.find((region) => region.value === storageSettings.cloud_region)
                    || (storageSettings.cloud_region
                      ? { value: storageSettings.cloud_region, label: storageSettings.cloud_region }
                      : null)
                  }
                  getOptionLabel={(option) => `${option.label} (${option.value})`}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  filterOptions={(options, state) => {
                    const query = state.inputValue.trim().toLowerCase();
                    if (!query) {
                      return options;
                    }
                    return options.filter((option) =>
                      option.label.toLowerCase().includes(query)
                      || option.value.toLowerCase().includes(query)
                    );
                  }}
                  disabled={fieldsDisabled}
                  openOnFocus
                  onChange={(_, option) => {
                    setStorageSettings(prev => ({
                      ...prev,
                      cloud_region: option?.value || '',
                    }));
                    if (errors.cloud_region) {
                      setErrors(prev => ({ ...prev, cloud_region: '' }));
                    }
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      modifiers: [
                        { name: 'flip', enabled: false },
                        {
                          name: 'preventOverflow',
                          options: {
                            altAxis: false,
                            tether: false,
                            padding: 8,
                          },
                        },
                      ],
                      sx: { zIndex: 1400 },
                    },
                    paper: {
                      sx: { mt: 0.5 },
                    },
                    listbox: {
                      sx: {
                        maxHeight: 220,
                        textAlign: 'left',
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cloud Region"
                      placeholder="Search region"
                      error={Boolean(errors.cloud_region)}
                      helperText={errors.cloud_region}
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Bucket Name"
                  value={storageSettings.bucket_name}
                  onChange={handleChange('bucket_name')}
                  disabled={fieldsDisabled}
                  error={Boolean(errors.bucket_name)}
                  helperText={errors.bucket_name}
                  variant="outlined"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Upload File Path"
                  value={storageSettings.upload_file_path}
                  onChange={handleChange('upload_file_path')}
                  disabled={fieldsDisabled}
                  error={Boolean(errors.upload_file_path)}
                  helperText={errors.upload_file_path || 'Example: recordings/ or uploads/app-files/'}
                  variant="outlined"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Access Key"
                  value={storageSettings.access_key}
                  onChange={handleChange('access_key')}
                  disabled={fieldsDisabled}
                  error={Boolean(errors.access_key)}
                  helperText={errors.access_key}
                  variant="outlined"
                  autoComplete="off"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Secret Key"
                  type={showSecretKey && isEditing ? 'text' : 'password'}
                  value={storageSettings.secret_key}
                  onChange={handleChange('secret_key')}
                  disabled={fieldsDisabled}
                  error={Boolean(errors.secret_key)}
                  helperText={errors.secret_key}
                  variant="outlined"
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecretKey(prev => !prev)}
                          disabled={fieldsDisabled}
                          size="small"
                          aria-label={showSecretKey ? 'Hide secret key' : 'Show secret key'}
                        >
                          {showSecretKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid2>
            </Grid2>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StorageSettings;
